/**
 * Role Switcher Component
 * Allows users with multiple roles to switch between roles on the fly
 */

import React, { useState, useRef, useEffect } from 'react';
import { useActiveRole } from '../../context/ActiveRoleContext';
import './RoleSwitcher.css';

export const RoleSwitcher: React.FC = () => {
  const { activeRole, availableRoles, setActiveRole, hasMultipleRoles, canSwitchRole } = useActiveRole();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to extract role name from string or object
  const getRoleName = (role: string | any): string => {
    if (typeof role === 'string') {
      return role;
    }
    if (role && typeof role === 'object') {
      return (role as any).roleName || (role as any).name || (role as any).role || String(role);
    }
    return String(role || '');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Don't render if user doesn't have multiple roles
  if (!canSwitchRole || !hasMultipleRoles) {
    return null;
  }

  const handleRoleSelect = (role: string) => {
    setActiveRole(role);
    setIsOpen(false);
  };

  return (
    <div className="role-switcher-container" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="role-switcher-button"
        aria-label="Switch role"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="role-switcher-button-content">
          <svg
            className="role-switcher-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="role-switcher-label">
            {activeRole ? getRoleName(activeRole) : 'Select Role'}
          </span>
          <svg
            className={`role-switcher-arrow ${isOpen ? 'open' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="role-switcher-dropdown">
          <div className="role-switcher-dropdown-header">
            <span className="role-switcher-dropdown-title">Switch Role</span>
            <span className="role-switcher-dropdown-subtitle">
              You have {availableRoles.length} role{availableRoles.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="role-switcher-dropdown-list">
            {availableRoles.map((role) => {
              const roleName = getRoleName(role);
              const roleKey = typeof role === 'string' ? role : getRoleName(role);
              return (
              <button
                key={roleKey}
                type="button"
                onClick={() => handleRoleSelect(roleName)}
                className={`role-switcher-option ${activeRole === roleName ? 'active' : ''}`}
                aria-label={`Switch to ${roleName} role`}
              >
                <span className="role-switcher-option-label">{roleName}</span>
                {activeRole === roleName && (
                  <svg
                    className="role-switcher-check"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

